import * as endpoints from "../../lib/api/endpoints";
import * as error from "../../lib/api/error";
import * as queryStringHelpers from "../../lib/queryStringHelpers";
import * as routes from "../../constants/routes";
import * as validationLib from "../../lib/validation";
import ActionButton, {ActionButtonStyle} from "../ActionButton";
import callApplicationLead from "../../lib/callApplicationLead";
import CheckboxEnhanced from "../ CheckboxEnhanced";
import cn from "../../lib/classnames";
import ErrorBoundary from "../ErrorBoundary";
import Footer from "../Footer";
import getUserIds from "../../lib/getUserIds";
import GreenCheck from "../icons/GreenCheck";
import IdleTimeoutWrapper from "../IdleTimeoutWrapper";
import LandingPageWrapper from "../LandingPageWrapper";
import miniGoldCard from "../../images/mini-gold-card.png";
import miniGoldCard2 from "../../images/mini-gold-card@2x.png";
import miniGoldCard3 from "../../images/mini-gold-card@3x.png";
import miniSecuredCard from "../../images/mini-secured-card.png";
import miniSecuredCard2 from "../../images/mini-secured-card@2x.png";
import miniSecuredCard3 from "../../images/mini-secured-card@3x.png";
import numberOne from "../../images/group-13.svg";
import numberTwo from "../../images/group-13-copy.svg";
import numberThree from "../../images/group-13-copy-2.svg";
import Quote from "../Quote";
import React, {useEffect, useRef, useState, useCallback} from "react";
import RedX from "../icons/RedX";
import ScrollLock from "../ScrollLock";
import SecuredInterestTable from "../SecuredInterestTable";
import SetNoIndex from "../../lib/SetNoIndex";
import Spinner from "../Spinner";
import styles from "./SecuredCardLandingPage.module.css";
import TextLink from "../TextLink";
import TitledContentBlocks from "../TitledContentBlocks";
import useLeadsRX from "../../lib/useLeadsRX";
import ValidationList from "../ValidationList";
import {ContentBlock} from "../TitledContentBlocks/TitledContentBlocks";
import {Container, Row, Column} from "../grid";
import {CounterOffer} from "../../lib/api/models";

const strings = {
    badRequest:
        "We were unable to find a secured card offer for this application. Please check your email for your secured card offer and follow the link to accept.",
    checkboxLabel:
        "I have read and agree to the Important Information above including the Security Agreement and Secured Credit Card Agreement",
    checkEmail: "Check your email for a message from 1st Financial Bank USA.",
    completeMy: "Complete my secured card application",
    contentBlockContent1:
        "Make a refundable deposit to your account (minimum $100). This is the amount you will be able to spend with your card.",
    contentBlockTitle1: "Set your own credit limit & fund deposit",
    contentBlockContent2:
        "Purchase gas, groceries, and other essentials anywhere Visa cards are accepted. Plus - You won’t be held liable for any purchases you didn’t make.",
    contentBlockTitle2: "Use your card anywhere",
    contentBlockContent3:
        "If you make at the least the minimum payment due on time every month and otherwise use your card responsibly, you may see your credit score begin to go up.",
    contentBlockTitle3: "Get on the road to good credit",
    decline: "Decline",
    electronicAgreement: "Electronic Communications Agreement",
    getMySecuredCard: "Get My 1FB Secured Card",
    goldCard: "gold card",
    learnMore: "Learn More about 1FB Secured Card",
    notApproved: "Not Approved for Gold Card",
    numberOne: "Number One",
    numberTwo: "Number Two",
    numberThree: "Number Three",
    openMyAccount: "Open My Account",
    otherErrors:
        "Something went wrong loading your secured card offer. Click the button below to try again.",
    pleaseRead: "Please read and agree to important information and disclosures",
    qualified:
        "You were not approved for a Gold card, but you have been prequalified for a secured card.",
    quote:
        "Renting your very own apartment or buying your dream car someday may require you to have good credit. But where do you start? For those with poor or limited credit history, don’t be discouraged. A secured card is a great first step for establishing credit. Purchasing items and making required payments on your account each month can help build your credit score AND develop smart money habits.",
    securityDepositAgreement: "Security Deposit Agreement",
    securedCard: "secured card",
    somethingWentWrong:
        "Something went wrong loading your secured card offer. Click the button below to find your offer.",
    title: "1st Financial Bank USA Secured Card Application",
    titledContentHeader: "How it works",
    tryAgain: "Try Again",
    unauthorized:
        "We don't have enough information to locate your secured card offer at this time. Please check your email for your secured card offer and follow the link to accept.",
    watch: "Watch for mail from 1st Financial Bank USA.",
    yesApprovedSecured: "Yes! Eligible for Secured Card",
};

/* Image sources provided by parent. Styles provided by child */
function getNumberOneImage(imageStyle?: string) {
    return <img className={imageStyle} src={numberOne} alt={strings.numberOne} />;
}
function getNumberTwoImage(imageStyle?: string) {
    return <img className={imageStyle} src={numberTwo} alt={strings.numberTwo} />;
}
function getNumberThreeImage(imageStyle?: string) {
    return <img className={imageStyle} src={numberThree} alt={strings.numberThree} />;
}

const contentBlocks: ContentBlock[] = [
    {
        renderImage: getNumberOneImage,
        title: strings.contentBlockTitle1,
        content: strings.contentBlockContent1,
    },
    {
        renderImage: getNumberTwoImage,
        title: strings.contentBlockTitle2,
        content: strings.contentBlockContent2,
    },
    {
        renderImage: getNumberThreeImage,
        title: strings.contentBlockTitle3,
        content: strings.contentBlockContent3,
    },
];

const EMPTY_COUNTER_OFFER: CounterOffer = {
    type: "",
    apr: -1,
    annualFee: -1,
    mpd: null,
    expDate: null,
    depositMin: "",
    depositMax: "",
    depositStep: "",
};

const LRX_CODE = 14397;
const COMPONENT_NAME = "SecuredCardLandingPage";

// Secured Card Status Codes
const SECURED_FORGOT = 401;

// Counter Offer Status Codes
const UNAUTHORIZED = 401;
const BAD_REQUEST = 400;

// Secured Card Decisions
enum Decisions {
    Accept = "accept",
    Decline = "decline",
}

export default function SecuredCardLandingPage() {
    useEffect(() => {
        document.title = strings.title;
    }, []);

    const windowUrl = window.location.href;

    const [counterOffer, setCounterOffer] = useState(EMPTY_COUNTER_OFFER);
    const [loadingApplication, setloadingApplication] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>("ERROR");
    const [econsent, setEconsent] = useState(false);

    const isMountedRef = useRef<boolean>(false);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Full round-trip
    function redirectUser(route: string) {
        if (!isMountedRef.current) {
            return;
        }
        const destination = `${process.env.REACT_APP_BASE_PATH}${route}${window.location.search}`;
        window.location.replace(destination);
    }

    const getApplication = useCallback(() => {
        setloadingApplication(true);
        endpoints
            .getApplication()
            .then((application) => {
                setCounterOffer(
                    application.counterOffer ? application.counterOffer : EMPTY_COUNTER_OFFER,
                );
                setEconsent(application.econsent);
                setErrorMessage(null);
            })
            .catch((err) => {
                const status: number = err.response.status;
                switch (status) {
                    case SECURED_FORGOT:
                        redirectUser(routes.SECURED_FORGOT);
                        break;
                    case UNAUTHORIZED:
                        setErrorMessage(strings.unauthorized);
                        break;
                    case BAD_REQUEST:
                        setErrorMessage(strings.badRequest);
                        break;
                    default:
                        setErrorMessage(strings.otherErrors);
                }
                error.reportAxios(err, "SecuredCardLandingPage");
            })
            .finally(() => setloadingApplication(false));
    }, []);

    useEffect(() => {
        getApplication();
    }, [getApplication]);

    SetNoIndex();
    useLeadsRX(LRX_CODE);

    useEffect(() => {
        const qs = queryStringHelpers.objectify(window.location.search);
        if (qs.m !== "300") {
            endpoints
                .visit(
                    getUserIds().visitID,
                    document.documentURI,
                    navigator.userAgent,
                    document.referrer,
                )
                .catch((err) => error.reportAxios(err, COMPONENT_NAME));
        }
    }, []);

    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [loadingDecline, setLoadingDecline] = useState(false);
    const [checkbox, setCheckbox] = useState(false);
    const [validation, setValidation] = useState<validationLib.Validation>({});

    function getSecuredCardImage() {
        return (
            <>
                <div className={cn(styles.qualified, styles.qualifiedMobile)}>
                    {strings.qualified}
                </div>
                <div className={styles.goldCardContainer}>
                    <RedX className={styles.icon} />
                    <img
                        className={styles.cardImage}
                        src={miniGoldCard}
                        srcSet={`${miniGoldCard} 1x, ${miniGoldCard2} 2x, ${miniGoldCard3} 3x`}
                        alt={strings.goldCard}
                    />
                    <div className={styles.cardText}>{strings.notApproved}</div>
                </div>
                <div className={styles.securedCardContainer}>
                    <GreenCheck className={styles.icon} />
                    <img
                        className={styles.cardImage}
                        src={miniSecuredCard}
                        srcSet={`${miniSecuredCard} 1x, ${miniSecuredCard2} 2x, ${miniSecuredCard3} 3x`}
                        alt={strings.securedCard}
                    />
                    <div className={styles.cardText}>{strings.yesApprovedSecured}</div>
                </div>
            </>
        );
    }

    function clearValidation() {
        setValidation((prev) => {
            const nextValidation = {
                ...prev,
            };
            delete nextValidation.generic;
            return nextValidation;
        });
    }

    function onSubmit(e: React.FormEvent) {
        e.preventDefault();

        callApplicationLead("secured-accept", null, windowUrl);
        setLoadingSubmit(true);
        clearValidation();

        endpoints
            .postSecured(Decisions.Accept)
            .then(() => {
                if (!isMountedRef.current) {
                    return;
                }
                redirectUser(routes.SECURED_DEPOSIT);
            })
            .catch((err) => {
                if (!isMountedRef.current) {
                    return;
                }
                const status: number = err.response.status;
                switch (status) {
                    case SECURED_FORGOT: {
                        redirectUser(routes.SECURED_FORGOT);
                        break;
                    }
                    default: {
                        const errValidation = validationLib.getValidationObj(err);
                        setValidation(errValidation);
                    }
                }
            })
            .finally(() => {
                if (!isMountedRef.current) {
                    return;
                }
                setLoadingSubmit(false);
            });
    }

    function onDecline() {
        callApplicationLead("secured-decline", null, windowUrl);
        setLoadingDecline(true);
        clearValidation();

        endpoints
            .postSecured(Decisions.Decline)
            .then(() => {
                if (!isMountedRef.current) {
                    return;
                }
                redirectUser(routes.THANK_YOU);
            })
            .catch((err) => {
                if (!isMountedRef.current) {
                    return;
                }
                const status: number = err.response.status;
                switch (status) {
                    case SECURED_FORGOT: {
                        redirectUser(routes.WELCOME_BACK);
                        break;
                    }
                    default: {
                        const errValidation = validationLib.getValidationObj(err);
                        setValidation(errValidation);
                    }
                }
            })
            .finally(() => {
                if (!isMountedRef.current) {
                    return;
                }
                setLoadingDecline(false);
            });
    }

    const contentBlockRef = React.createRef<HTMLDivElement>();
    const completeMyRef = React.createRef<HTMLDivElement>();

    function renderCounterOffer() {
        return !errorMessage ? (
            <ScrollLock>
                <div className={styles.table}>
                    <SecuredInterestTable
                        apr={counterOffer.apr}
                        annualFee={counterOffer.annualFee}
                    />
                </div>
            </ScrollLock>
        ) : (
            <>
                <div className={styles.somethingWentWrong}>{errorMessage}</div>
                <ActionButton
                    style={ActionButtonStyle.primary}
                    onClick={getApplication}
                    label={strings.tryAgain}
                    type="button"
                />
            </>
        );
    }

    function renderBannerChildren() {
        return (
            <div className={styles.bannerContainer}>
                <div className={cn(styles.qualifiedDesktop, styles.qualified)}>
                    {strings.qualified}
                </div>
                <div className={styles.bannerButtons} id="bannerButtons">
                    <ActionButton
                        className={cn(styles.buttonPlaceholder, styles.learnMoreButton)}
                        labelClassName={cn(styles.buttonPlaceholder, styles.buttonLabel)}
                        onClick={() => {
                            callApplicationLead("secured-learn-more-button", null, windowUrl);
                            contentBlockRef.current?.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                                inline: "start",
                            });
                        }}
                        style={ActionButtonStyle.outlinedDark}
                        label={strings.learnMore}
                        fullWidth={true}
                        type="button"
                    />
                    <ActionButton
                        className={cn(styles.buttonPlaceholder, styles.geyMyCardButton)}
                        labelClassName={cn(styles.buttonPlaceholder, styles.buttonLabel)}
                        onClick={() => {
                            callApplicationLead("secured-get-1fb-card-button", null, windowUrl);
                            completeMyRef.current?.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                                inline: "start",
                            });
                        }}
                        style={ActionButtonStyle.secondary}
                        label={strings.getMySecuredCard}
                        fullWidth={true}
                        type="button"
                    />
                </div>
            </div>
        );
    }

    function renderLandingPage() {
        return (
            <>
                <LandingPageWrapper
                    renderInterestRates={false}
                    onlyRenderCardBanner={true}
                    renderBlueGradient={true}
                >
                    <div className={styles.topDiv}>
                        <Container>
                            <Row>
                                <Column
                                    large={5}
                                    largeOffset={1}
                                    medium={4}
                                    className={styles.image}
                                >
                                    <div className={styles.cardsWrapper}>
                                        {getSecuredCardImage()}
                                    </div>
                                </Column>
                                <Column large={5} medium={4}>
                                    {renderBannerChildren()}
                                </Column>
                            </Row>
                        </Container>
                    </div>
                </LandingPageWrapper>

                <Quote quote={strings.quote} />

                <div ref={contentBlockRef}>
                    <TitledContentBlocks
                        header={strings.titledContentHeader}
                        blocks={contentBlocks}
                    />
                </div>
                <form onSubmit={onSubmit}>
                    <Container>
                        <Row>
                            <Column large={12} medium={8} className={styles.container}>
                                <div ref={completeMyRef} className={styles.tableTitle}>
                                    {strings.completeMy}
                                </div>
                                <div className={styles.agreementText}>{strings.pleaseRead}</div>
                            </Column>
                        </Row>
                        <Row>
                            <Column
                                large={12}
                                medium={8}
                                className={
                                    !errorMessage && !loadingApplication
                                        ? styles.tableContainer
                                        : styles.container
                                }
                            >
                                {!loadingApplication ? (
                                    renderCounterOffer()
                                ) : (
                                    <div className={styles.loadingApplication}>
                                        <Spinner
                                            isVisible={true}
                                            className={styles.loaderAppearance}
                                        />
                                    </div>
                                )}
                            </Column>
                        </Row>
                        <Row>
                            <Column large={12} medium={8} className={styles.container}>
                                {!loadingApplication && errorMessage === null ? (
                                    <CheckboxEnhanced
                                        label={strings.checkboxLabel}
                                        hasGradient={true}
                                        isChecked={checkbox}
                                        required={true}
                                        onChange={(e) => setCheckbox(e.target.checked)}
                                    >
                                        {!econsent ? (
                                            <>
                                                {" "}
                                                as well as the{" "}
                                                <TextLink
                                                    className={styles.checkboxLink}
                                                    openInSameTab={false}
                                                    to={routes.ELECTRONIC_AGREEMENT_FULL}
                                                    text={strings.electronicAgreement}
                                                />
                                            </>
                                        ) : null}
                                    </CheckboxEnhanced>
                                ) : null}
                            </Column>
                        </Row>
                        <Row>
                            <Column
                                largeOffset={3}
                                large={9}
                                medium={8}
                                className={styles.buttonContainer}
                            >
                                {!loadingApplication && errorMessage === null ? (
                                    <div
                                        className={cn(
                                            styles.buttonColumn,
                                            validation.generic ? undefined : styles.buttonMargin,
                                        )}
                                    >
                                        <ActionButton
                                            style={ActionButtonStyle.outlined}
                                            label={strings.decline}
                                            fullWidth={true}
                                            onClick={() => {
                                                onDecline();
                                            }}
                                            loading={loadingDecline}
                                            disabled={
                                                loadingSubmit ||
                                                counterOffer === EMPTY_COUNTER_OFFER
                                            }
                                            type="button"
                                            className={styles.declineButton}
                                        />
                                        <ActionButton
                                            style={ActionButtonStyle.primary}
                                            label={strings.openMyAccount}
                                            fullWidth={true}
                                            loading={loadingSubmit}
                                            disabled={
                                                loadingDecline ||
                                                counterOffer === EMPTY_COUNTER_OFFER
                                            }
                                            type="submit"
                                            className={styles.openButton}
                                        />
                                    </div>
                                ) : null}
                            </Column>
                        </Row>
                        {validation.generic ? (
                            <Row>
                                <Column
                                    large={12}
                                    medium={8}
                                    className={styles.validationContainer}
                                >
                                    <div className={styles.validation}>
                                        <ValidationList
                                            className={styles.validation}
                                            messages={validation.generic}
                                        />
                                    </div>
                                </Column>
                            </Row>
                        ) : null}
                    </Container>
                </form>
                <Footer />
            </>
        );
    }

    return (
        <ErrorBoundary>
            {loadingApplication ? (
                renderLandingPage()
            ) : (
                <IdleTimeoutWrapper
                    focusAfterContinue={null}
                    noLink={true}
                    description={econsent ? strings.checkEmail : strings.watch}
                >
                    {renderLandingPage()}
                </IdleTimeoutWrapper>
            )}
        </ErrorBoundary>
    );
}
